import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Card,
  SvgIcon,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { ReactComponent as NoConnectionImage } from "../../images/NoConnectionImage.svg";

import { ReactComponent as JiraIcon } from "../../images/jira-logo.svg";
import { ReactComponent as AzureIcon } from "../../images/azure-logo.svg";
import { ReactComponent as GithubIcon } from "../../images/github-logo.svg";
import { ReactComponent as qTestIcon } from "../../images/qtest-logo.svg";
import { ReactComponent as TestrailIcon } from "../../images/testrail-logo.svg";
import { ReactComponent as SlackIcon } from "../../images/slack-logo.svg";
import { ReactComponent as ConfluenceIcon } from "../../images/confluence-logo.svg";

import UserSettings from "./UserSettings";
import {
  getUserConfig,
  deleteUserConfig,
} from "../../services/webServices";

const apps = [
  { id: "Jira", name: "Jira", icon: JiraIcon },
  { id: "Azure", name: "Azure", icon: AzureIcon },
  { id: "Github", name: "Github", icon: GithubIcon },
  { id: "qTest", name: "qTest", icon: qTestIcon },
  { id: "TestRail", name: "TestRail", icon: TestrailIcon },
  { id: "Slack", name: "Slack", icon: SlackIcon },
  { id: "Confluence", name: "Confluence", icon: ConfluenceIcon },
];

const urlKeys = {
  Jira: "jiraUrl",
  Azure: "adoUrl",
  qTest: "qtest_url",
  TestRail: "testRailUrl",
  Confluence: "confluenceUrl"
};

const UserConfiguration = () => {
  const [selectedApp, setSelectedApp] = useState("Jira");
  const [connection, setConnection] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [refreshFlag, setRefreshFlag] = useState(false);
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    async function fetchConfig() {
      try {
        const response = await getUserConfig({ tool: selectedApp });
        console.log('API Response:', response.data);
        
        if (response.data && response.data.configuration) {
          const config = response.data.configuration;
          const url = config[urlKeys[selectedApp]] || "";
          const connectionName = config.connectionName || `${selectedApp} Connection`;
          
          setConnection({
            url,
            name: connectionName,
            fullConfig: config,
            mapping_id: response.data.mapping_id,
            tool: response.data.tool,
            username: response.data.username
          });
        } else {
          setConnection(null);
        }
      } catch (error) {
        console.error('Error fetching config:', error);
        setConnection(null);
      }
    }
    fetchConfig();
  }, [selectedApp, refreshFlag]);

  const handleSelectApp = (appName) => {
    if ([
      "Jira",
      "Azure",
      "Github",
      "qTest",
      "TestRail",
      "Slack",
      "Confluence",
    ].includes(appName)) {
      setSelectedApp(appName);
    }
  };

  const handleEditConnection = () => {
    setIsDrawerOpen(true);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteUserConfig(selectedApp);
      setConnection(null);
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete config:', error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleAddConnection = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setTimeout(() => {
      setRefreshFlag(flag => !flag);
    }, 100);
  };

  const renderEmptyState = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
        textAlign: "center",
        mt: 4,
      }}
    >
      <SvgIcon
        component={NoConnectionImage}
        inheritViewBox
        sx={{
          width: 120,
          height: 120,
          mb: 0,
          mr: 4,
          color: "#49A3FF",
        }}
      />
      <Typography
        variant="h6"
        sx={{
          color: "#666",
          mb: 2,
          fontWeight: "normal",
        }}
      >
        No connection established yet
      </Typography>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleAddConnection}
        sx={{
          bgcolor: "#49A3FF",
          color: "white",
          textTransform: "none",
          borderRadius: 2,
          px: 3,
          py: 1,
          "&:hover": {
            bgcolor: "#369BF4",
          },
        }}
      >
        Add connection
      </Button>
    </Box>
  );

  const renderDeleteConfirmationDialog = () => (
    <Dialog
      open={deleteDialogOpen}
      onClose={handleDeleteCancel}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          p: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
          fontWeight: 600,
          fontSize: "1.25rem",
        }}
      >
        Delete connection
        <IconButton
          onClick={handleDeleteCancel}
          sx={{ color: "#666" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pb: 3 }}>
        <Typography color="#666">
          Are you sure you want to delete the {selectedApp} connection?
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
        <Button
          onClick={handleDeleteCancel}
          variant="outlined"
          sx={{
            textTransform: "none",
            color: "#49A3FF",
            borderColor: "#49A3FF",
            borderRadius: 2,
            px: 3,
            "&:hover": {
              borderColor: "#369BF4",
              color: "#369BF4",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDeleteConfirm}
          variant="contained"
          sx={{
            textTransform: "none",
            bgcolor: "#49A3FF",
            color: "white",
            borderRadius: 2,
            px: 3,
            "&:hover": {
              bgcolor: "#369BF4",
            },
          }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          bgcolor: "#F5F5F2",
          minHeight: "calc(100vh - 150px)",
          overflowY: "auto",
          maxHeight: "100vh",
        }}
      >
        <Container
          component="main"
          maxWidth={false}
          sx={{ marginLeft: "10px", marginRight: "10px" }}
        >
          <Box sx={{ my: 1, overflow: "hidden" }}>
            <Box
              sx={{
                my: 0.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                overflow: "hidden",
              }}
            >
              <Typography variant="h6" fontWeight="normal" fontSize="11">
                Available app configurations
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Choose any one application to manage your projects and tasks.
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                gap: 2,
                justifyContent: "flex-start",
                bgcolor: "#fff",
                borderRadius: 2,
                p: 2,
                mt: 2,
                overflowX: "auto",
              }}
            >
              {apps.map((app) => (
                <Box key={app.name} sx={{ textAlign: "center" }}>
                  <Card
                    variant="outlined"
                    sx={{
                      cursor: "pointer",
                      textAlign: "left",
                      height: "90px",
                      width: "125px",
                      border:
                        selectedApp === app.name
                          ? "2px solid #49A3FF"
                          : "1px solid #ddd",
                      "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      flexDirection: "column",
                      pl: 2,
                    }}
                    onClick={() => handleSelectApp(app.name)}
                  >
                    <SvgIcon
                      sx={{ height: "35px", width: "45px" }}
                      component={app.icon}
                      inheritViewBox
                    />
                    <Typography
                      variant="h2"
                      sx={{
                        mt: 2,
                        color: "#000000",
                        fontSize: "15px",
                      }}
                    >
                      {app.name}
                    </Typography>
                  </Card>
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 3, bgcolor: "#fff", borderRadius: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                  mt: 2,
                  mr: 2,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="normal"
                  fontSize="11"
                  sx={{ ml: 2 }}
                >
                  {selectedApp} Connection
                </Typography>
              </Box>
              
              {!connection ? (
                renderEmptyState()
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 2,
                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#fafafa" }}>
                        <TableCell sx={{ fontWeight: 600, color: "#666" }}>
                          URL
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#666" }}>
                          Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#666" }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        sx={{
                          "&:hover": { bgcolor: "rgba(0, 0, 0, 0.02)" },
                          borderBottom: "1px solid #e0e0e0",
                        }}
                      >
                        <TableCell sx={{ color: "#333", fontSize: "14px" }}>
                          {connection.url}
                        </TableCell>
                        <TableCell sx={{ color: "#333", fontSize: "14px" }}>
                          {connection.name}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={handleEditConnection}
                              sx={{
                                color: "#49A3FF",
                                "&:hover": {
                                  bgcolor: "rgba(73, 163, 255, 0.1)",
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleDeleteClick}
                              sx={{
                                color: "#49A3FF",
                                "&:hover": {
                                  bgcolor: "rgba(255, 68, 68, 0.1)",
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Delete Confirmation Dialog */}
      {renderDeleteConfirmationDialog()}

      {/* UserSettings drawer */}
      <UserSettings
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        selectedApp={selectedApp}
        editMode={!!connection}
        existingConfig={connection?.fullConfig}
      />
    </>
  );
};

export default UserConfiguration;